/* eslint-disable no-console */
import AnimatedMainRoutes from "@app/components/routing/AnimatedMainRoutes/AnimatedMainRoutes"
import { useGetStrings } from "@app/store/service/cmsService"
import { useCallback, useEffect, useRef, useState } from "react"

// Route configuration for main navigation pages - maps routes to CMS string keys
const ROOT_ROUTES = {
	"/": "zoneSelectionPageTitle",
	"/login": "login",
	"/create-account": "createAccountPageTitle",
	"/forgot-password": "forgotPasswordPageSubTitle",
	"/order-succeed": "orderSucceedPageTitle",
	"/order-failed": "orderFailedPageTitle",
}

// Route configuration for order flow - maps route names to CMS string keys
const ORDER_ROUTES = {
	"person-types": "guestSelectionLabel",
	"date-selection": "dateSelectionLabel",
	"lounger-selection": "loungerSelectionPageTitle",
	"loungermap-selection": "loungerMapSelectionPageTitle",
	"bluphoria-selection": "bluphoriaSelectionPageTitle",
	"order-overview": "orderOverviewTitle",
	"payment-methods": "paymentMethodsPageTitle",
	"address-details": "addressDetailsPageTitle",
	"next-order-overview": "orderConfirmationPageTitle",
}

import ProtectedRoute from "@app/components/routing/ProtectedRoute/ProtectedRoute"
import CashregisterUtilitiesModal from "@app/modals/CashregisterUtilitiesModal/CashregisterUtilitiesModal"
import LinkModal from "@app/modals/kiosk/LinkModal"
import NotificationInfoModal from "@app/modals/NotificationInfoModal"
import AppDataPage from "@app/pages/app-data"
import AppEmployeePointsPage from "@app/pages/app-employeepoints"
import AppOrderHistoryPage from "@app/pages/app-order-history/AppOrderHistoryPage"
import AppThermencardPage from "@app/pages/app-thermencard"
import AppTransponderPage from "@app/pages/app-transponder"
import EmployeeFunnel from "@app/pages/employeeFunnel/EmployeeFunnel"
import ConfirmAccount from "@app/pages/funnel/account/ConfirmAccount/ConfirmAccountPage"
import CreateAccountPage from "@app/pages/funnel/account/CreateAccount/CreateAccountPage"
import ForgotPasswordPage from "@app/pages/funnel/account/ForgotPassword/ForgotPasswordPage"
import LoginPage from "@app/pages/funnel/account/Login/LoginPage"
import NewPassword from "@app/pages/funnel/account/NewPasswordPage/NewPasswordPage"
import NotVerifiedaccount from "@app/pages/funnel/account/NotVerifiedaccount/NotVerifiedaccount"
import { FunnelSelection } from "@app/pages/funnel/FunnelSelection/FunnelSelection"
import KioskEntrancePage from "@app/pages/funnel/kiosk/KioskEntrancePage"
import { KioskSucceedPage } from "@app/pages/funnel/kiosk/KioskSucceedPage"
import { OrderFailed } from "@app/pages/funnel/order/OrderFailed/OrderFailed"
import { OrderSucceed } from "@app/pages/funnel/order/OrderSucceed/OrderSucceed"
import OrderPage from "@app/pages/funnel/order/Root/OrderPage"
import ZoneSelectionPage from "@app/pages/funnel/ZoneSelection/ZoneSelectionPage"
import MaintenacePage from "@app/pages/MaintenacePage/MaintenacePage"
import MassageBooking from "@app/pages/massage/MassageBooking"
import Account from "@app/pages/myParadise/account/Account"
import { MyParadiseOverview } from "@app/pages/myParadise/overview/MyParadiseOverview"
import OnSiteLoungerBookingPage from "@app/pages/onSiteLounger"
import TabletImmersiveRoomBooking1 from "@app/pages/tablet-immersive-room/tablet-immersive-room-1"
import TabletImmersiveRoomBooking2 from "@app/pages/tablet-immersive-room/tablet-immersive-room-2"
import TabletImmersiveRoomBooking3 from "@app/pages/tablet-immersive-room/tablet-immersive-room-3"
import TabletImmersiveRoomBooking4 from "@app/pages/tablet-immersive-room/tablet-immersive-room-4"
import TableSaunaBooking1 from "@app/pages/tablet-sauna-boat/tablet-sauna-boat-1"
import TableSaunaBooking2 from "@app/pages/tablet-sauna-boat/tablet-sauna-boat-2"
import TableSaunaBooking3 from "@app/pages/tablet-sauna-boat/tablet-sauna-boat-3"
import TableSaunaBooking4 from "@app/pages/tablet-sauna-boat/tablet-sauna-boat-4"
import TabletSurfWave1 from "@app/pages/tablet-surf-wave/tablet-surf-wave-1"
import TabletSurfWave2 from "@app/pages/tablet-surf-wave/tablet-surf-wave-2"
import TabletSurfWave3 from "@app/pages/tablet-surf-wave/tablet-surf-wave-3"
import TabletSurfWave4 from "@app/pages/tablet-surf-wave/tablet-surf-wave-4"
import { useRaiseError } from "@app/store/actions/errorActions"
import { appStore } from "@app/store/store"
import { isWebView } from "@app/utils/isWebView"
import localStore from "@app/utils/localStore"
import tracking from "@app/utils/tracking"
import { KioskBridge } from "@tecreation/bird5-kiosk-bridge"
import { Modal, useModal } from "@tecreation/funnel-components"
import { Route, useLocation } from "react-router"
import { useCustomNavigation } from "./NavigationHandler"

export default function RootNavigation() {
	const kioskResetTimer = useRef(null)
	const closeBtnWasVisible = useRef(false)
	const navigation = useCustomNavigation()
	const location = useLocation()
	const htgGuestData = appStore.get("core.htgGuestData")
	const { showModal } = useModal()
	const raiseError = useRaiseError()
	const { data: strings = {} } = useGetStrings(undefined, { defaultData: {} })
	const { buttonClose, playstoreQRAltText, appStoreQRAltText } = strings

	// State for screen reader announcements
	const [pageTitle, setPageTitle] = useState("")

	// const { data: overlayData } = useGetOverlay();

	useEffect(() => {
		if (!appStoreQRAltText || !playstoreQRAltText) return

		const overlayData = {
			overlayId: 1,
			disableCache: false,
			disableClose: false,
			desktopTitle: `<h1 style="font-size: 20px">Buche jetzt über unsere neue BLUPHORIA App und sichere dir viele Vorteile!</h1>`,
			desktopContent: `
            <div style="padding: 0 20px">
                <ul style="list-style: unset; text-align: left">
                    <li style="margin-bottom: 10px">Buche dein Ticket bequem Zuhause und checke über unsere Fastlane ein und aus</li>
                    <li style="margin-bottom: 10px">Werde MyBLUPHORIA Member, genieße exklusive Vorteile und sammle mit jeder Buchung Palmen</li>
                    <li style="margin-bottom: 10px">Entdecke Yoga-Videos, AI-generierte Musik, erfrischende Sinnesreisen und vieles mehr</li>
                </ul>
                <div style="margin-top: 20px">
                    <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px">Jetzt downloaden und Thermenerlebnis verbessern!</h2>
                    <div style="display: flex; justify-content: center">
                        <div style="margin-right: 20px;display: flex; justify-content: center; flex-direction: column; align-items: center">
                            <img src="${QR_APPLE}" alt="${appStoreQRAltText}" style="height: 100px; width: 100px"/>
                            <div style="font-size: 16px; text-align: center">Zum Apple App-Store</div>
                        </div>
                        <div style="display: flex; justify-content: center; flex-direction: column; align-items: center">
                            <img src="${QR_Android}" alt="${playstoreQRAltText}" style="height: 100px; width: 100px"/>
                            <div style="font-size: 16px; text-align: center">Zum Android Playstore</div>
                        </div>
                    </div>
                </div>
            </div>`,
			mobileTitle: `<h1 style="font-size: 20px">Buche jetzt über unsere neue BLUPHORIA App und sichere dir viele Vorteile!</h1>`,
			mobileContent: `
            <div style="padding: 0 20px">
                <ul style="list-style: unset; text-align: left">
                    <li style="margin-bottom: 10px">Buche dein Ticket bequem Zuhause und checke über unsere Fastlane ein und aus</li>
                    <li style="margin-bottom: 10px">Werde MyBLUPHORIA Member, genieße exklusive Vorteile und sammle mit jeder Buchung Palmen</li>
                    <li style="margin-bottom: 10px">Entdecke Yoga-Videos, AI-generierte Musik, erfrischende Sinnesreisen und vieles mehr</li>
                </ul>
                <div style="margin-top: 20px">
                    <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px">Jetzt downloaden und Thermenerlebnis verbessern!</h2>
                    <div style="display: flex; flex-direction: column">
                        <div onclick="window.open('https://apps.apple.com/de/app/bluphoria-deine-wellnesswelt/id6449082273','_blank');" style="margin-bottom: 20px;display: flex; justify-content: center; flex-direction: column; align-items: center; background: url('${Btn_Apple}'); height: 42px;background-position: center; background-repeat: no-repeat">
                        </div>
                        <div onclick="window.open('https://play.google.com/store/search?q=bluphoria&c=apps&gl=DE','_blank');"style="display: flex; justify-content: center; flex-direction: column; align-items: center; background: url('${Btn_Android}'); height: 42px;background-position: center; background-repeat: no-repeat">
                        </div>
                    </div>
                </div>
            </div>`,
		}
		const usedModalIds = JSON.parse(localStore.getItem("usedModalIds") || "[]")
		//only if our current path is the ticket entrance page
		console.log(location)
		if (
			(usedModalIds.indexOf(overlayData.overlayId) === -1 || overlayData.disableCache == true) &&
			["/", "/myparadise"].indexOf(location.pathname.toLowerCase()) !== -1 &&
			!htgGuestData
		) {
			showModal(NotificationInfoModal, overlayData)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [strings])

	const getPageAnnouncement = (pathname, strings) => {
		// Order pages: /order/<step>
		const { newPageAnnouncement, newOrderSubPageLabel } = strings

		if (pathname.startsWith("/order/")) {
			const step = pathname.split("/")[2]
			const titleKey = ORDER_ROUTES[step]

			if (titleKey) {
				return `${newOrderSubPageLabel} ${strings[titleKey]}`
			}
		}

		// Root routes
		const titleKey = ROOT_ROUTES[pathname]
		if (titleKey && strings[titleKey]) {
			return `${newPageAnnouncement} ${strings[titleKey]}`
		}
	}

	/**
	 * That effect handles the visibillity of the custom button that is sometimes needed in an ifram (mostly 7d)
	 * if the button was one time set to display:block we save that value and then we show the button on the main page
	 * otherwise if open a different page we set display to none so that the button is not shown anymore (was a wish from 7d)
	 */
	useEffect(() => {
		window.appStore = appStore
		if (location.pathname !== "/") {
			if (document.getElementById("funnelIframeCloseBtn").style.display === "block") {
				closeBtnWasVisible.current = true
			}
			document.getElementById("funnelIframeCloseBtn").style.display = "none"

			if ((location.pathname === "/order-succeed" || location.pathname === "/order-failed") && closeBtnWasVisible.current) {
				document.getElementById("funnelIframeCloseBtn").style.display = "block"
			}
		} else {
			if (closeBtnWasVisible.current === true) {
				document.getElementById("funnelIframeCloseBtn").style.display = "block"
			}
		}

		if (appStore.get("core.frameSource") === "7d" || isWebView()) {
			if (
				location.pathname === "/app-data" ||
				location.pathname === "/myParadise" ||
				location.pathname === "/app-order-history" ||
				location.pathname === "/order-succeed" ||
				location.pathname === "/order-failed"
			) {
				document.getElementById("funnelIframeCloseBtn").style.display = "block"
			}
		}

		tracking.pageView(location.pathname)
	}, [location.pathname])

	useEffect(() => {
		if (!strings || !Object.keys(strings).length) return

		const announcement = getPageAnnouncement(location.pathname, strings)

		if (announcement) setPageTitle(announcement)
	}, [location.pathname, strings])

	const kioskModeResetClick = useCallback(() => {
		clearTimeout(kioskResetTimer.current)
		kioskResetTimer.current = setTimeout(() => {
			navigation.push("/kiosk-entrance")
		}, 35 * 1000)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleKioskLinks = useCallback(e => {
		const origin = e.target.closest(`a`)

		if (origin) {
			const href = origin.href
			e.preventDefault()
			showModal(LinkModal, { link: href })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (appStore.get("core.kioskMode")) {
			window.addEventListener("click", kioskModeResetClick)
			window.addEventListener("keyup", kioskModeResetClick)
			document.addEventListener(`click`, handleKioskLinks)
		} else if (appStore.get("core.frameSource") == "7d") {
			document.addEventListener(`click`, handleKioskLinks)
		}

		if (window.self !== window.top) {
			KioskBridge.onLogin(function () {
				showModal(CashregisterUtilitiesModal)
			})
			if (appStore.get("core.kioskMode")) {
				KioskBridge.execute("cashregister.kiosk.getCashregister")
					.then(cashregister => {
						appStore.set("core.cashregister", cashregister)
					})
					.catch(() => {
						alert("there was a problem while loading the application.. please try again")
					})
			}
		}

		window.addEventListener("unhandledrejection", function (e) {
			console.log("unhandled rejection")
			console.log(e)
			raiseError(e.reason, undefined, true)
		})

		return () => {
			window.removeEventListener("click", kioskModeResetClick)
			document.removeEventListener(`click`, handleKioskLinks)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			{/* ARIA live region for route change announcements (WCAG SC 4.1.3) */}
			<div role="status" className="sr-only">
				{pageTitle}
			</div>

			{/* This element exists only for application that emmbeds the funnel insinde an iframe and need a close button (7D App) */}
			<div style={{ position: "absolute", right: "0", height: "32px", width: "32px", display: "none", zIndex: 99 }} id="funnelIframeCloseBtn">
				<Modal.Closer ariaLabel={buttonClose} />
			</div>

			<AnimatedMainRoutes>
				<Route path="/php-error" element={<MaintenacePage />} />
				<Route path="/" element={<ZoneSelectionPage />} />
				<Route path="/login" element={<LoginPage />} />
				<Route path="/create-account" element={<CreateAccountPage />} />
				<Route path="/forgot-password" element={<ForgotPasswordPage />} />
				<Route path="/new-password" element={<NewPassword />} />
				<Route path="/confirm-account" element={<ConfirmAccount />} />
				<Route path="/account-not-verified" element={<NotVerifiedaccount />} />
				<Route path="/myParadise" element={<Account />} />
				<Route path="/myParadise/overview" element={<MyParadiseOverview />} />
				<Route path="/order-succeed" element={<OrderSucceed />} />
				<Route path="/order-failed" element={<OrderFailed />} />
				<Route path="/massage" element={<MassageBooking />} />
				<Route path="/employeeFunnel" element={<EmployeeFunnel />} />
				<Route path="/order/*" element={<OrderPage />} />
				<Route path="/kiosk-entrance" element={<KioskEntrancePage />} />
				<Route path="/kiosk-succeed" element={<KioskSucceedPage />} />
				<Route path="/app-data" element={<AppDataPage />} />
				<Route path="/app-order-history" element={<AppOrderHistoryPage />} />
				<Route path="/app-thermencard" element={<AppThermencardPage />} />
				<Route path="/app-transponder" element={<AppTransponderPage />} />
				<Route path="/app-employeepoints" element={<AppEmployeePointsPage />} />
				<Route path="/on-site-lounger-booking" element={<OnSiteLoungerBookingPage />} />
				<Route
					path="/funnel-selection"
					element={
						<ProtectedRoute path="/funnel-selection">
							<FunnelSelection />
						</ProtectedRoute>
					}
				/>
				<Route path="/tablet-sauna-boat-1" element={<TableSaunaBooking1 />} />
				<Route path="/tablet-sauna-boat-2" element={<TableSaunaBooking2 />} />
				<Route path="/tablet-sauna-boat-3" element={<TableSaunaBooking3 />} />
				<Route path="/tablet-sauna-boat-4" element={<TableSaunaBooking4 />} />
				<Route path="/tablet-immersive-room-1" element={<TabletImmersiveRoomBooking1 />} />
				<Route path="/tablet-immersive-room-2" element={<TabletImmersiveRoomBooking2 />} />
				<Route path="/tablet-immersive-room-3" element={<TabletImmersiveRoomBooking3 />} />
				<Route path="/tablet-immersive-room-4" element={<TabletImmersiveRoomBooking4 />} />
				<Route path="/tablet-surf-wave-1" element={<TabletSurfWave1 />} />
				<Route path="/tablet-surf-wave-2" element={<TabletSurfWave2 />} />
				<Route path="/tablet-surf-wave-3" element={<TabletSurfWave3 />} />
				<Route path="/tablet-surf-wave-4" element={<TabletSurfWave4 />} />
			</AnimatedMainRoutes>
		</>
	)
}

const QR_APPLE = "img/apple.png"
const QR_Android = "img/android.png"
const Btn_Apple = "img/apple_btn.png"
const Btn_Android = "img/android_btn.png"
